import { palateHeight, palateSection } from './palate';
import { FLOOR_OF_MOUTH, ROOF } from './tongueTargets';

/**
 * The mouth's lining for the 3D model (TongueScene), as one smooth surface around the cavity: the
 * measured palate on top (palate.ts), the cheeks outside the teeth, the floor of the mouth below,
 * deepening at the back where the tongue's root sits. The model stays inside the mouth (no throat:
 * too much for children). Millimetres, x to the speaker's left, y up, z forward; beyond the
 * measured midline and arch, sizes are estimated for a child's mouth.
 */

export interface Surface {
  /** [x, y, z] per vertex, mm. */
  positions: Float32Array;
  /** Opacity per vertex (0-1). */
  alpha: Float32Array;
  index: number[];
}

/** Where the soft palate ends (its tip, mm [z, y]): the uvula hangs from here. */
export const VELUM_TIP: readonly [number, number] = ROOF[ROOF.length - 1] ?? [-78.5, 11.8];

/** Height of the floor of the mouth along the midline at depth z (mm), interpolated. */
function floorAt(z: number): number {
  const first = FLOOR_OF_MOUTH[0];
  const last = FLOOR_OF_MOUTH[FLOOR_OF_MOUTH.length - 1];
  if (!first || !last) return -15;
  if (z >= first[0]) return first[1];
  if (z <= last[0]) return last[1];
  for (let i = 0; i + 1 < FLOOR_OF_MOUTH.length; i++) {
    const [z0, y0] = FLOOR_OF_MOUTH[i] ?? first;
    const [z1, y1] = FLOOR_OF_MOUTH[i + 1] ?? last;
    if (z <= z0 && z >= z1) return y0 + ((y1 - y0) * (z - z0)) / Math.min(-1e-6, z1 - z0);
  }
  return last[1];
}

/** How far the floor deepens at the back (mm), where the tongue's root sits. */
const ROOT_POCKET = 8;

/** The mouth cavity from just behind the front teeth to where it opens into the throat (mm). */
const ORAL_FRONT = -6;
const ORAL_BACK = -70;
/** How far the cheeks lie outside the palate's edge (mm): room for the teeth and gums. */
const CHEEK = 11;
/** The occlusal plane (where the teeth meet), front to back: height at the sides (mm). */
const occlusal = (z: number) => -0.5 + (6 * Math.min(1, Math.max(0, -z - 6))) / 40;

/**
 * The mouth cavity's lining as one surface: per depth a closed ring around the cavity, the
 * palate's vault on top (palate.ts), the cheeks at the sides outside the teeth, and the floor of
 * the mouth below, rising toward the sides; open at the front (behind the lips) and at the back
 * (into the throat). Rows front to back, `around` points per ring (starting at the bottom middle).
 */
export function oralCavity(around = 48, rows = 34, front = ORAL_FRONT, back = ORAL_BACK): Surface {
  const positions = new Float32Array(rows * around * 3);
  const alpha = new Float32Array(rows * around).fill(1);
  for (let r = 0; r < rows; r++) {
    const z = front + ((back - front) * r) / (rows - 1);
    // In front of the palate (inside the lips) and behind it, the nearest section continues.
    const zq = Math.min(ORAL_FRONT, Math.max(ORAL_BACK, z));
    const section = palateSection(zq) ?? { halfWidth: 13, edgeY: 15 };
    const cheek = section.halfWidth + CHEEK * Math.min(1, Math.max(0.35, (-z - 2) / 18));
    const side = occlusal(z);
    // Below the floor of the mouth at the back sits the tongue's root (down toward the hyoid):
    // the cavity deepens there so the root stays inside it.
    const root = Math.min(1, Math.max(0, (-z - 48) / 18));
    const floor = floorAt(z) - 2 - ROOT_POCKET * root * root;
    for (let c = 0; c < around; c++) {
      // Angle around the ring from the bottom middle (0) through the speaker's left side.
      const angle = (c / around) * Math.PI * 2;
      const sx = Math.sin(angle);
      const top = Math.cos(angle) < 0;
      const ax = Math.abs(sx);
      let x: number;
      let y: number;
      if (top) {
        // Upper half: across the palate's vault, then out and down to the cheek.
        const u = ax;
        const inner = u * cheek;
        if (inner <= section.halfWidth) y = palateHeight(zq, inner) ?? section.edgeY;
        else {
          const k = (inner - section.halfWidth) / Math.max(1e-6, cheek - section.halfWidth);
          y = section.edgeY + (side - section.edgeY) * Math.sin((k * Math.PI) / 2);
        }
        x = Math.sign(sx) * inner;
      } else {
        // Lower half: the floor, rising to the cheeks at the sides.
        const u = ax;
        x = Math.sign(sx) * u * cheek;
        y = floor + (side - floor) * u ** 2.2;
      }
      positions.set([x, y, z], (r * around + c) * 3);
    }
  }
  const index: number[] = [];
  for (let r = 0; r + 1 < rows; r++)
    for (let c = 0; c < around; c++) {
      const a = r * around + c;
      const b = r * around + ((c + 1) % around);
      index.push(a, b, a + around, b, b + around, a + around);
    }
  return { positions, alpha, index };
}

/**
 * The mouth cavity's outline at the midline (mm, [z, y]): from inside the upper lip over the front
 * teeth and along the roof, round the back of the mouth, and forward along the floor to inside the
 * lower lip (the hole the classic cutaway leaves open in the cut face, so the front teeth show).
 */
export function cavityOutline(step = 2): [number, number][] {
  const top: [number, number][] = [
    [3, 8],
    [-1, 11],
  ];
  const bottom: [number, number][] = [];
  for (let z = ORAL_FRONT; z >= ORAL_BACK; z -= step) {
    top.push([z, palateHeight(z, 0) ?? 12]);
    const root = Math.min(1, Math.max(0, (-z - 48) / 18));
    bottom.push([z, floorAt(z) - 2 - ROOT_POCKET * root * root]);
  }
  // Round the back of the mouth: from the soft palate down to the floor, bulging back.
  const roofEnd = top[top.length - 1] ?? [ORAL_BACK, 12];
  const floorEnd = bottom[bottom.length - 1] ?? [ORAL_BACK, -30];
  const back: [number, number][] = [];
  for (let k = 1; k < 8; k++) {
    const t = k / 8;
    back.push([ORAL_BACK - 7 * Math.sin(Math.PI * t), roofEnd[1] + (floorEnd[1] - roofEnd[1]) * t]);
  }
  return [...top, ...back, ...bottom.reverse(), [-1, -11], [3, -8]];
}
